import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useCallback } from "react";

export interface CatalogPlan {
  id: string;
  name: string;
  price_monthly: number;
  features: Record<string, boolean | number>;
}

export function usePlanCatalog() {
  const { data: rawData, isLoading } = useQuery({
    queryKey: ["plan-features-catalog"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_plan_features_catalog");
      if (error) {
        console.error("get_plan_features_catalog error:", error);
        return [];
      }
      return (data as any[]) ?? [];
    },
    staleTime: 10 * 60 * 1000,
  });

  const plans: CatalogPlan[] = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];
    return rawData.map((row: any) => {
      const featuresObj: Record<string, boolean | number> = {};
      const feats = row.features as any[];
      if (Array.isArray(feats)) {
        for (const f of feats) {
          if (f.limit_value != null) {
            featuresObj[f.feature_key] = f.limit_value;
          } else {
            featuresObj[f.feature_key] = f.is_enabled ?? false;
          }
        }
      }
      return {
        id: row.plan_id,
        name: row.plan_name,
        price_monthly: row.price_monthly ?? 0,
        features: featuresObj,
      };
    });
  }, [rawData]);

  const getPlanByName = useCallback(
    (name: string) => plans.find((p) => p.name.toLowerCase() === name.toLowerCase()),
    [plans],
  );

  const getMinPlanForFeature = useCallback(
    (featureKey: string): string | undefined => {
      const matching = plans
        .filter((p) => {
          const val = p.features[featureKey];
          if (val === undefined || val === false) return false;
          return true;
        })
        .sort((a, b) => a.price_monthly - b.price_monthly);
      return matching[0]?.name;
    },
    [plans],
  );

  return { plans, getPlanByName, getMinPlanForFeature, isLoading };
}
